import { withZod } from '@remix-validated-form/with-zod'
import { z } from 'zod'
import { zfd } from 'zod-form-data'

import { legacyRoleRank } from 'app/types/roles'

/**
 * Client validators for the user details form (UserDetailsComponent).
 *
 * Restored from the pre-SPA Remix route `details.tsx`, which defined these
 * inline and passed them to the component. The Remix-to-SPA migration
 * (commit 80564ba1d) stripped the route's loaders/actions and dropped the
 * validators, leaving the form with `validator={undefined}` — which crashed
 * ValidatedForm on submit (TRK-6070). The server-side `action` validator
 * (with the email-availability check) is not part of this file: the SPA
 * submit path is a separate concern.
 */
const userSchema = (isNameRequired: boolean = true) =>
  z
    .object({
      id: z.string(),
      name: isNameRequired ? z.string().min(1, { message: `Name is required` }) : z.string().optional(),
      _aliases: z.string().optional(),
      position: z
        .enum([
          `Executive`,
          `Consultant`,
          `Dentist`,
          `Hygienist`,
          `Manager`,
          `Treatment Coordinator`,
          `Administrator`,
          `Other`,
          ``,
        ])
        .optional(),
      jobTitle: z.string().or(z.undefined()).optional(),
      email: z.string().trim().min(5, { message: `Email is required` }).email(`Must be a valid email`),
      role: zfd.numeric(
        z
          .number()
          .gte(legacyRoleRank.workspaceUser, { message: `Role is not valid` })
          .lte(legacyRoleRank.orgAdmin, { message: `Role is not valid` }),
      ),
      roleId: z.string().optional(),
      staffId: z.object({ id: z.string(), name: z.string() }).optional(),
      allWorkspaces: zfd.checkbox().optional(),
      allLocations: zfd.checkbox().optional(),
      assignedWorkspaceIds: zfd.repeatable(z.array(zfd.text())).optional(),
      allowedLocationIds: zfd.repeatable(z.array(zfd.text())).optional(),
      orgId: z.string(),
      workspaceId: z.string().optional(),
      type: z.enum([`super`, `standard`]).optional(),
      phone: z.string().optional(),
      resend: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.position === `Other` && !data.jobTitle) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Please add the position title`,
          path: [`jobTitle`],
        })
      }
    })

export const editUserValidator = withZod(userSchema()) as any

// New-user client validator. Name is optional here because the server-side
// invite flow fills it; the client only blocks a bad email and role.
export const newUserValidatorClient = withZod(userSchema(false)) as any
